// Implements: F-UIX-019
/**
 * Money as text for the admin views, from the integer micros the API speaks.
 *
 * This is the admin module's own formatter (UIX.9: money appears only in backoffice and
 * admin components). The amount is built from the integer's digits, never divided as a
 * float, and handed to `Intl` as a decimal string, so a large total is never rounded by
 * the conversion. Up to six decimals are kept, as the API counts them. A currency `Intl`
 * does not know is shown as the plain amount and its code, never as an exception.
 */

import { PLATFORM_LOCALE } from "../../../types/locale";

const PLACES = 6;

/** `1234500` -> `"1.234500"`, `-5` -> `"-0.000005"`: exact, from the integer's digits. */
export function microsDecimal(micros: number): string {
  if (!Number.isSafeInteger(micros)) throw new TypeError("Money is a whole number of micros.");
  const digits = Math.abs(micros).toString().padStart(PLACES + 1, "0");
  const sign = micros < 0 ? "-" : "";
  return `${sign}${digits.slice(0, -PLACES)}.${digits.slice(-PLACES)}`;
}

/**
 * One amount in its currency, in the platform locale ("USD 1,000.50", `types/locale.ts`). An amount beyond exact integers
 * (never sent by the API) degrades to plain text instead of breaking the view.
 */
export function costText(micros: number, currency: string): string {
  if (!Number.isSafeInteger(micros)) return `${String(micros / 10 ** PLACES)} ${currency}`;
  const decimal = microsDecimal(micros);
  try {
    return new Intl.NumberFormat(PLATFORM_LOCALE, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: PLACES,
    }).format(decimal as Intl.StringNumericLiteral);
  } catch {
    return `${decimal} ${currency}`;
  }
}
